// =============================================================================
// Priority Spel V3 — Minigame ⇄ shell contract (central definition)
// -----------------------------------------------------------------------------
// The shell (views/GameView.vue) owns the SPELUITLEG and SCORE phases plus the
// quit confirmation and the result/stars processing. A registered game component
// owns ONLY the "spelen" (play) phase and communicates through this contract:
//
//   input : `meta`  (a MINIGAMES entry, see config/gameConfig.js) as a prop
//           `paused` (optional Boolean) — the shell sets it true while its
//                    quit-confirm is open so real-time games can pause. Optional:
//                    games that ignore it keep working.
//   output: emit('finished', <FinishedPayload>) exactly once per play session
//           emit('quit')  — ask the shell to open its "spel verlaten" confirm
//                           (§5.2). It does NOT mean reset/finish/leave directly.
//
// Stars are resolved centrally by the backend/mock via resolveStars(meta.starRules,
// payload) — never in a game or adapter. See config/gameConfig.js:resolveStars.
//
// @typedef {Object} FinishedScore     A score-based result.
// @property {number} score            Raw game score (0 is valid — never reject on
//                                      a truthy check). Central rules map it to stars.
// @property {boolean} [completed]      The round finished (defaults true server-side).
//
// @typedef {Object} FinishedAttempts  An attempts-based result (Wordle / puzzle).
// @property {number} attempts          Fewer attempts → more stars, per meta.starRules.
// @property {boolean} completed         Must be true for a completed round.
//
// @typedef {FinishedScore | FinishedAttempts} FinishedPayload
// =============================================================================

/**
 * Defensive contract check used by the shell before processing a `finished`
 * event. Accepts either allowed shape. A score of `0` is a valid, finite number
 * and MUST pass — do not use a truthy test on score/attempts anywhere.
 *
 * @param {unknown} payload
 * @returns {boolean} true when the payload carries a usable score or attempts.
 */
export function isValidFinishedPayload(payload) {
  if (!payload || typeof payload !== 'object') return false
  return Number.isFinite(payload.score) || Number.isFinite(payload.attempts)
}
